import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiConstants } from '../constants/api-constants';

export type AttendanceStatus = 'early' | 'on_time' | 'late';
export type AttendanceAction = 'check_in' | 'check_out';

export interface AttendanceScanEvent {
  at: number;
  status: AttendanceStatus;
  scannedBy?: { id: string; username?: string; firstName?: string; lastName?: string } | null;
}

export interface AttendanceStudent {
  id: string;
  firstNameAr?: string;
  lastNameAr?: string;
  firstNameEn?: string;
  lastNameEn?: string;
  studentCode?: string;
  class?: { id: string; name: string } | null;
}

export interface AttendanceRecord {
  id: string;
  date: string;
  student: AttendanceStudent | null;
  checkIn: AttendanceScanEvent | null;
  checkOut: AttendanceScanEvent | null;
}

export interface AttendanceDayResult {
  date: string;
  items: AttendanceRecord[];
}

export type AttendanceDayStatus = 'absent' | 'checked_in' | 'complete';

export interface AttendanceReportRow {
  studentId: string;
  studentCode: string;
  studentNameAr: string;
  studentNameEn: string;
  classId: string;
  className: string;
  dayStatus: AttendanceDayStatus;
  checkIn: AttendanceScanEvent | null;
  checkOut: AttendanceScanEvent | null;
  attendanceId: string | null;
}

export interface AttendanceScheduleInfo {
  checkInOpen: string;
  checkInLateAfter: string;
  checkOutOpen: string;
  checkOutLateAfter: string;
  lateEnabled?: boolean;
  holidayWeekdays?: number[];
  holidayDates?: { name: string; date: string }[];
  byWeekday?: Record<string, {
    checkInOpen: string;
    checkInLateAfter: string;
    checkOutOpen: string;
    checkOutLateAfter: string;
  }>;
  isHoliday?: boolean;
}

export interface AttendanceReport {
  date: string;
  schedule: AttendanceScheduleInfo;
  summary: {
    total: number;
    present: number;
    checked_in: number;
    complete: number;
    absent: number;
    check_in_early: number;
    check_in_on_time: number;
    check_in_late: number;
    check_out_early: number;
    check_out_on_time: number;
    check_out_late: number;
  };
  rows: AttendanceReportRow[];
}

export type AttendanceDayMark = 'present' | 'absent' | 'late' | 'holiday';

export interface AttendanceRangeRow {
  studentId: string;
  studentCode: string;
  studentNameAr: string;
  studentNameEn: string;
  classId: string;
  className: string;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  attendanceRate: number;
  dayMarks: Record<string, AttendanceDayMark>;
}

export interface AttendanceRangeReport {
  from: string;
  to: string;
  days: string[];
  countedDays?: string[];
  schedule: AttendanceScheduleInfo;
  summary: {
    totalStudents: number;
    dayCount: number;
    present: number;
    absent: number;
    late: number;
    attendanceRate: number;
    absenceRate: number;
  };
  rows: AttendanceRangeRow[];
}

export interface AttendanceScanResult {
  ok?: boolean;
  alreadyScanned?: boolean;
  reason?: 'already_checked_in' | 'already_checked_out' | null;
  action: AttendanceAction;
  date: string;
  scanTime: string;
  schedule: AttendanceScheduleInfo;
  attendance: AttendanceRecord;
  student: AttendanceStudent;
}

@Injectable({ providedIn: 'root' })
export class AttendanceService {
  private get url() { return ApiConstants.URL_ATTENDANCE; }

  constructor(private http: HttpClient) {}

  async scan(qrCode: string, action: AttendanceAction): Promise<AttendanceScanResult> {
    const response = await firstValueFrom(
      this.http.post<any>(`${this.url}/scan`, { qrCode, action })
    );
    return response.data;
  }

  async manualMark(studentId: string, action: AttendanceAction): Promise<AttendanceScanResult> {
    const response = await firstValueFrom(
      this.http.post<any>(`${this.url}/manual`, { studentId, action })
    );
    return response.data;
  }

  async today(classId?: string): Promise<AttendanceDayResult> {
    const qs = classId ? `?class=${encodeURIComponent(classId)}` : '';
    const response = await firstValueFrom(this.http.get<any>(`${this.url}/today${qs}`));
    return response.data;
  }

  async listByDate(date: string, classId?: string): Promise<AttendanceDayResult> {
    const params = new URLSearchParams({ date });
    if (classId) {
      params.set('class', classId);
    }
    const response = await firstValueFrom(
      this.http.get<any>(`${this.url}?${params.toString()}`)
    );
    return response.data;
  }

  async getReport(date?: string, classId?: string): Promise<AttendanceReport> {
    const params = new URLSearchParams();
    if (date) {
      params.set('date', date);
    }
    if (classId) {
      params.set('class', classId);
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await firstValueFrom(this.http.get<any>(`${this.url}/report${qs}`));
    return response.data;
  }

  async getRangeReport(from: string, to: string, classId?: string): Promise<AttendanceRangeReport> {
    const params = new URLSearchParams({ from, to });
    if (classId) {
      params.set('class', classId);
    }
    const response = await firstValueFrom(
      this.http.get<any>(`${this.url}/report?${params.toString()}`)
    );
    return response.data;
  }
}
